import {
  CheckCircleIcon as CheckCircleIconSolid,
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
} from '@heroicons/react/24/solid'
import { RefObject } from 'react'
import { KEYPOINT_COLORS } from '../../constants/colors'
import { COCOAnnotation } from '../../types'
import { cocoFormatToKeypoints } from '../../utils/cocoFormat'
import { getKeypointDisplayOrder } from '../../utils/keypointOrder'

interface KeypointSelectorProps {
  keypointNames: string[]
  currentKeypoint: number
  selectedAnnotation: COCOAnnotation | null
  onKeypointSelect: (index: number) => void
  onClearKeypoint: (index: number) => void
  onClearAll: () => void
  scrollRef?: RefObject<HTMLDivElement>
}

export default function KeypointSelector({
  keypointNames,
  currentKeypoint,
  selectedAnnotation,
  onKeypointSelect,
  onClearKeypoint: _onClearKeypoint,
  onClearAll,
  scrollRef,
}: KeypointSelectorProps) {
  const keypoints = selectedAnnotation ? cocoFormatToKeypoints(selectedAnnotation.keypoints) : []

  const getKeypointState = (index: number) => {
    if (!selectedAnnotation || index >= keypoints.length) return 'not_labeled'
    const kp = keypoints[index]
    if (kp.x < 0 || kp.y < 0) return 'not_labeled'
    return kp.visibility || 'visible'
  }

  const getKeypointStatus = () => {
    if (!selectedAnnotation) return { set: 0, total: keypointNames.length }

    const set = keypoints.filter(kp => kp.x >= 0 && kp.y >= 0).length
    return { set, total: keypointNames.length }
  }

  const status = getKeypointStatus()

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Fixed Header */}
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Keypoints</h3>

        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm ${selectedAnnotation ? 'text-gray-600' : 'text-gray-400'}`}>
            {status.set} / {status.total} set
          </span>
          <button
            onClick={onClearAll}
            className={`text-xs ${
              selectedAnnotation && status.set > 0
                ? 'text-red-600 hover:text-red-700 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            disabled={!selectedAnnotation || status.set === 0}
          >
            Clear All
          </button>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              selectedAnnotation ? 'bg-green-600' : 'bg-gray-300'
            }`}
            style={{ width: `${(status.set / status.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Scrollable Keypoint list */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2">
        {getKeypointDisplayOrder(keypointNames).map((dataIndex, _displayIndex) => {
          const name = keypointNames[dataIndex]
          const isCurrent = dataIndex === currentKeypoint
          const disabled = !selectedAnnotation

          return (
            <div
              key={dataIndex}
              id={`keypoint-${dataIndex}`}
              className={`
                flex items-center justify-between px-2 py-2 rounded-md cursor-pointer
                transition-colors
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${isCurrent && !disabled ? 'bg-blue-100 border-2 border-blue-500' : 'border-2 border-transparent'}
                ${!isCurrent && !disabled ? 'hover:bg-gray-50' : ''}
              `}
              onClick={() => !disabled && onKeypointSelect(dataIndex)}
            >
              <div className="flex items-center">
                <span
                  className="
                    inline-block w-6 h-6 rounded-full mr-3 text-xs
                    flex items-center justify-center font-medium text-white
                  "
                  style={{ backgroundColor: KEYPOINT_COLORS[dataIndex % KEYPOINT_COLORS.length] }}
                >
                  {dataIndex + 1}
                </span>
                <span className={`text-sm ${isCurrent && !disabled ? 'font-semibold' : ''}`}>
                  {name}
                </span>
              </div>

              <div className="flex items-center">
                {(() => {
                  const state = getKeypointState(dataIndex)
                  switch (state) {
                    case 'visible':
                      return <CheckCircleIconSolid className="w-5 h-5 text-green-600" />
                    case 'inferred':
                      return <QuestionMarkCircleIconSolid className="w-5 h-5 text-red-600" />
                    default:
                      return <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                  }
                })()}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
